import { useRef } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { Button } from 'components/common';
import { DropdownButton } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';

import type { ButtonInterface } from './Button';

interface CopyButtonInterface extends ButtonInterface {
  buttonStyle?: React.CSSProperties;
  customCopy?: (ev: React.SyntheticEvent, Function) => void;
  element?: string;
  isDropdown?: boolean;
  successNoticeText?: string;
  successText?: string;
  value?: string;
  valuePromise?: () => Promise<string>;
}

const CopyButton = ({
  buttonStyle,
  customCopy,
  element = 'input',
  icon = ['far', 'copy'],
  isDropdown,
  style,
  successNoticeText,
  successText,
  text,
  value,
  valuePromise,
  ...props
}: CopyButtonInterface) => {
  const { dispatch } = useDispatch();
  const { messages, theme } = useReduxState();

  const buttonContainer = useRef<HTMLDivElement>(undefined);

  const copyPromise = async (ev: React.PointerEvent<HTMLButtonElement>) => {
    try {
      if (typeof ClipboardItem && navigator.clipboard?.write) {
        const text = new ClipboardItem({
          'text/plain': valuePromise().then(
            (text) => new Blob([text], { type: 'text/plain' })
          )
        });
        navigator.clipboard.write([text]);
      } else {
        const text = await valuePromise();
        navigator.clipboard.writeText(text);
      }

      if (successText) {
        const copySuccess = buttonContainer?.current?.querySelector(
          '.copy-button-success'
        ) as HTMLButtonElement;
        copySuccess.style.animation = 'none';
        copySuccess.offsetHeight;
        copySuccess.style.animation = '';
      }

      if (successNoticeText) {
        dispatch(
          actions.addNotice({
            text: successNoticeText,
            type: 'success'
          })
        );
      }

      // click outside dropdown to close it
      if (isDropdown) setTimeout(() => document.body.click(), 500);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const copy = async (
    ev: React.PointerEvent<HTMLButtonElement>,
    customValue: string
  ) => {
    // try to use the new Clipboard API
    const clipboard = navigator.clipboard;
    if (clipboard && clipboard.writeText) {
      await clipboard.writeText(customValue ? customValue : value);

      const copySuccess = buttonContainer?.current?.querySelector(
        '.copy-button-success'
      ) as HTMLButtonElement;
      copySuccess.style.animation = 'none';
      copySuccess.offsetHeight;
      copySuccess.style.animation = '';
    } else {
      // check if we are inside modal
      const containerEl =
        getModalContainer(buttonContainer?.current) || document.body;

      // create temp textarea to copy text from
      const input = document.createElement(element) as HTMLInputElement;
      input.style.position = 'fixed';
      input.style.top = '-1000px';
      input.style.left = '-1000px';
      input.value = customValue ? customValue : value;
      containerEl.appendChild(input);
      input.select();

      try {
        document.execCommand('copy');

        const copySuccess = buttonContainer?.current?.querySelector(
          '.copy-button-success'
        ) as HTMLButtonElement;
        copySuccess.style.animation = 'none';
        copySuccess.offsetHeight;
        copySuccess.style.animation = '';
      } catch (err) {}

      containerEl.removeChild(input);

      // click outside dropdown to close it
      if (isDropdown) setTimeout(() => document.body.click(), 500);
    }
  };

  const modifyCopy = (ev) => customCopy(ev, copy);

  const getModalContainer = (initialEl) => {
    let el = initialEl;
    while (el && el.classList && !el.classList.contains('modal-container')) {
      el = el.parentElement;
    }
    if (el && el.classList && el.classList.contains('modal-container')) {
      return el;
    }
    return null;
  };

  return (
    <div className="copy-button" ref={buttonContainer} style={style}>
      {isDropdown ? (
        <DropdownButton
          data-keep-open={true}
          icon={icon}
          onClick={valuePromise ? copyPromise : customCopy ? modifyCopy : copy}
          text={
            <>
              {text}
              <div
                className="copy-button-success"
                style={{ animation: 'none' }}
              >
                {successText}
              </div>
            </>
          }
        />
      ) : (
        <Button
          {...props}
          onClick={valuePromise ? copyPromise : customCopy ? modifyCopy : copy}
          style={buttonStyle}
          text={
            <>
              {icon ? (
                <Icon
                  icon={icon}
                  style={{ marginRight: text ? '5px' : '0px' }}
                />
              ) : null}
              {text}
              <div
                className="copy-button-success"
                style={{ animation: 'none' }}
              >
                {successText}
              </div>
            </>
          }
        />
      )}

      <style jsx>{`
        .copy-button {
          display: inline-block;
          position: relative;
        }
        @keyframes copysuccess {
          from {
            opacity: 0;
            top: 0;
          }
          to {
            opacity: 1;
            top: -20px;
          }
        }
        .copy-button-success {
          animation: copysuccess 0.5s;
          color: ${theme.successStandaloneColor};
          font-size: 1.2rem;
          font-weight: 600;
          left: -5px;
          opacity: 0;
          position: absolute;
          text-align: center;
          top: 0;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default CopyButton;
